import { useState } from 'react'
import { parse } from 'papaparse'
import { Icon } from '@iconify/react'

import { DraggerUploader, Spin, useMessage } from '@pepper/ui'
import Footer from 'components/idea-lab/ImportModalFooter'

import { useCreateBulkKeywords } from 'utils/hooks/useCreateBulkKeywords'
import { pluralize } from '@pepper/utils/src/function/pluralize'
import { downloadSample, getBulkImportMsg } from '../../../utils'

import s from './Uploader.module.css'

type Props = {
  companyId: string
  projectId: string
  showFooter: boolean
  onClose: () => void
  onBack: () => void
  onChange: () => void
  setIsAddingBulkKeywords: (value: boolean) => void
}

export const Uploader: React.FC<Props> = ({
  onBack,
  onClose,
  onChange,
  companyId,
  projectId,
  showFooter,
  setIsAddingBulkKeywords,
}) => {
  const [keywords, setKeywords] = useState<string[]>([])
  const [isUploadError, setIsUploadError] = useState(false)
  const { showError, showSuccess, showWarning } = useMessage()

  const { mutateAsync: addBulkKeywords, isLoading: isBulkAdditionLoading } =
    useCreateBulkKeywords()

  const handleBulkAddKeywords = async () => {
    setIsAddingBulkKeywords(true)
    const response = await addBulkKeywords({
      companyId,
      projectId,
      keywords,
    })

    setIsAddingBulkKeywords(false)
    if (response?.error) {
      showError(response.error)
    } else {
      const totalAdded = response?.records?.length || 0
      const totalSkipped = keywords.length - totalAdded
      const msg = getBulkImportMsg({
        type: 'Keyword',
        totalAdded,
        totalSkipped,
      })

      if (totalAdded === 0) {
        showWarning(msg)
      } else {
        showSuccess(msg)
      }

      setKeywords([])
      onClose()
    }
  }

  const formatPayload = (value: { data: string[] }) => {
    if (value.data[0][0].toUpperCase() !== 'KEYWORDS') {
      setIsUploadError(true)
      return
    }
    const payload = value.data
      .filter((_, index) => index > 0)
      .map(keyword => {
        return keyword[0]?.trim()
      })
      .filter(keyword => keyword?.length > 0)

    const trimmedRequestPayload = payload.filter(
      keywordItem => keywordItem.length > 0
    )

    if (trimmedRequestPayload.length > 0) {
      setKeywords(trimmedRequestPayload)
    } else {
      setIsUploadError(true)
    }
  }

  const handleFileUpload = (file?: {
    originFileObj: NodeJS.ReadableStream
  }) => {
    setIsUploadError(false)
    setKeywords([])
    if (!file) return
    onChange()
    parse(file.originFileObj, {
      complete: formatPayload,
      error: () => {
        setIsUploadError(true)
      },
    })
  }

  const handleBack = () => {
    onBack()
    setKeywords([])
  }

  return (
    <Spin spinning={isBulkAdditionLoading}>
      <div className={s.body}>
        <div className={s.description}>
          <DraggerUploader
            showUploadStatusToast={false}
            onFileRemove={() => setKeywords([])}
            showList={!isUploadError}
            handleFiles={handleFileUpload}
            allowedFiles={['.csv']}
            caption="Click to Upload"
            className={s.link}
            info={
              <>
                Upload
                <span className={s.fileType}>&nbsp;.csv file (12MB max)</span>
              </>
            }
          />
        </div>
        <div className={s.suggestion}>
          The rows in the file should be in a particular order.
          <button
            className={s.link}
            onClick={() =>
              downloadSample({
                sampleRecord: [{ keywords: ['KEYWORDS'] }],
                sampleFileName: 'keywords-sample.csv',
              })
            }
          >
            Download Sample File
          </button>
        </div>
        {isUploadError && (
          <>
            <div className={s.errorInfo}>
              File upload was unsuccessful. Please resolve following issues and
              upload again:
            </div>
            <div className={s.error}>
              <Icon icon="lucide:info" width="14" height="14" /> File
              doesn&apos;t match the format
            </div>
            <div className={s.error}>
              <Icon icon="lucide:info" width="14" height="14" /> You cannot
              leave mandatory fields blank
            </div>
          </>
        )}
      </div>
      {showFooter && (
        <Footer
          disabled={keywords.length === 0}
          loading={isBulkAdditionLoading}
          onBack={handleBack}
          onCancel={onClose}
          onOk={handleBulkAddKeywords}
          okText={
            keywords.length > 0
              ? `Add ${pluralize(keywords.length, 'Keyword')}`
              : 'Add Keywords'
          }
        />
      )}
    </Spin>
  )
}
